// src/pages/house/HouseConnectionApprovalPage.jsx

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Modal,
  Pagination,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import {
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Eye,
  RefreshCcw,
  Search,
  XCircle,
} from "react-feather";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import { toast } from "react-toastify";

import {
  approveHouseConnectionRequestThunk,
  clearHouseConnectionAction,
  clearHouseConnectionError,
  clearSelectedHouseConnectionRequest,
  fetchHouseConnectionRequestByIdThunk,
  fetchHouseConnectionRequestsThunk,
  rejectHouseConnectionRequestThunk,
} from "../../../store/slices/houseConnectionSlice";

import "../../../css/master-common.css";

// ============================================================
// HELPERS
// ============================================================

const safe = (
  value,
  fallback = "—"
) =>
  value === null ||
  value === undefined ||
  value === ""
    ? fallback
    : value;

const money = (value) => {
  const n = Number(value || 0);

  return `₹${Math.round(
    Number.isFinite(n) ? n : 0
  ).toLocaleString("en-IN")}`;
};

const formatAction = (value) => {
  const action = String(value || "")
    .trim()
    .toLowerCase();

  if (action === "disconnection") {
    return "Disconnection";
  }

  if (action === "reconnection") {
    return "Reconnection";
  }

  return safe(value);
};

const formatStatus = (value) => {
  const status = String(value || "")
    .trim()
    .toLowerCase();

  if (status === "pending") {
    return "Pending";
  }

  if (status === "approved") {
    return "Approved";
  }

  if (status === "rejected") {
    return "Rejected";
  }

  return safe(value);
};

const formatDateTime = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return safe(value);
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDate = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return safe(value);
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

// ============================================================
// REUSABLE UI
// ============================================================

const StatusBadge = ({ status }) => {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();

  return (
    <Badge
      className={`hca-status ${
        normalized === "approved"
          ? "approved"
          : normalized === "rejected"
          ? "rejected"
          : "pending"
      }`}
    >
      {formatStatus(status)}
    </Badge>
  );
};

const DetailItem = ({
  label,
  value,
  accent = false,
}) => (
  <div className="hca-detail-item">
    <div className="hca-detail-label">
      {label}
    </div>

    <div
      className={`hca-detail-value ${
        accent ? "accent" : ""
      }`}
    >
      {safe(value)}
    </div>
  </div>
);

// ============================================================
// PAGE
// ============================================================

export default function HouseConnectionApprovalPage() {
  const dispatch =
    useDispatch();

  const {
    rows,
    selectedRequest,
    pagination,
    loading,
    detailLoading,
    approving,
    rejecting,
    error,
    actionSuccess,
    actionMessage,
  } = useSelector(
    (state) =>
      state.houseConnection
  );

  const permissions =
    useSelector(
      (state) =>
        state.permissions?.map
          ?.house_connection_approvals
    ) || {};

  const canView =
    permissions.can_view !==
    false;

  const canUpdate =
    permissions.can_update !==
    false;

  // ==========================================================
  // FILTERS
  // ==========================================================

  const [
    filters,
    setFilters,
  ] = useState({
    search: "",
    status: "pending",
    action_type: "",
    assigned_to_me: true,
    page: 1,
    pageSize: 10,
  });

  const [
    searchInput,
    setSearchInput,
  ] = useState("");

  // ==========================================================
  // MODALS
  // ==========================================================

  const [
    detailModal,
    setDetailModal,
  ] = useState(false);

  const [
    actionModal,
    setActionModal,
  ] = useState({
    show: false,
    type: "",
    request: null,
  });

  const [
    approvalRemarks,
    setApprovalRemarks,
  ] = useState("");

  // ==========================================================
  // LOAD
  // ==========================================================

  const loadRequests = (
    overrides = {}
  ) => {
    if (!canView) {
      return;
    }

    dispatch(
      fetchHouseConnectionRequestsThunk({
        ...filters,
        ...overrides,
      })
    );
  };

  useEffect(() => {
    if (!canView) {
      return;
    }

    dispatch(
      fetchHouseConnectionRequestsThunk(
        filters
      )
    );
  }, [
    dispatch,
    filters.page,
    filters.pageSize,
    filters.status,
    filters.action_type,
    filters.assigned_to_me,
    canView,
  ]);

  // ==========================================================
  // TOASTS
  // ==========================================================

  useEffect(() => {
    if (!error) {
      return;
    }

    toast.error(
      String(error)
    );

    dispatch(
      clearHouseConnectionError()
    );
  }, [
    error,
    dispatch,
  ]);

  useEffect(() => {
    if (!actionSuccess) {
      return;
    }

    toast.success(
      actionMessage ||
        "Request updated successfully"
    );

    dispatch(
      clearHouseConnectionAction()
    );

    setActionModal({
      show: false,
      type: "",
      request: null,
    });

    setApprovalRemarks("");

    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    actionSuccess,
    actionMessage,
    dispatch,
  ]);

  // ==========================================================
  // SEARCH
  // ==========================================================

  const submitSearch = (
    event
  ) => {
    event?.preventDefault();

    const value =
      searchInput.trim();

    setFilters(
      (previous) => ({
        ...previous,
        search: value,
        page: 1,
      })
    );

    dispatch(
      fetchHouseConnectionRequestsThunk({
        ...filters,
        search: value,
        page: 1,
      })
    );
  };

  const clearSearch = () => {
    setSearchInput("");

    setFilters(
      (previous) => ({
        ...previous,
        search: "",
        page: 1,
      })
    );

    dispatch(
      fetchHouseConnectionRequestsThunk({
        ...filters,
        search: "",
        page: 1,
      })
    );
  };

  // ==========================================================
  // DETAILS
  // ==========================================================

  const openDetails =
    async (request) => {
      if (!canView) {
        toast.error(
          "No view permission"
        );
        return;
      }

      setDetailModal(true);

      const result =
        await dispatch(
          fetchHouseConnectionRequestByIdThunk(
            request.request_id
          )
        );

      if (
        fetchHouseConnectionRequestByIdThunk
          .rejected
          .match(result)
      ) {
        setDetailModal(false);
      }
    };

  const closeDetails = () => {
    setDetailModal(false);

    dispatch(
      clearSelectedHouseConnectionRequest()
    );
  };

  // ==========================================================
  // APPROVE / REJECT
  // ==========================================================

  const openActionModal = (
    request,
    type
  ) => {
    if (!canUpdate) {
      toast.error(
        "No update permission"
      );
      return;
    }

    setApprovalRemarks("");

    setActionModal({
      show: true,
      type,
      request,
    });
  };

  const closeActionModal =
    () => {
      if (
        approving ||
        rejecting
      ) {
        return;
      }

      setActionModal({
        show: false,
        type: "",
        request: null,
      });

      setApprovalRemarks("");
    };

  const submitAction =
    async () => {
      const request =
        actionModal.request;

      if (!request?.request_id) {
        toast.error(
          "Request ID is missing"
        );
        return;
      }

      if (
        actionModal.type ===
          "reject" &&
        !approvalRemarks.trim()
      ) {
        toast.error(
          "Rejection remarks are required"
        );
        return;
      }

      let result;

      if (
        actionModal.type ===
        "approve"
      ) {
        result =
          await dispatch(
            approveHouseConnectionRequestThunk({
              request_id:
                request.request_id,

              approval_remarks:
                approvalRemarks.trim(),
            })
          );

        if (
          approveHouseConnectionRequestThunk
            .rejected
            .match(result)
        ) {
          return;
        }
      } else {
        result =
          await dispatch(
            rejectHouseConnectionRequestThunk({
              request_id:
                request.request_id,

              approval_remarks:
                approvalRemarks.trim(),
            })
          );

        if (
          rejectHouseConnectionRequestThunk
            .rejected
            .match(result)
        ) {
          return;
        }
      }
    };

  // ==========================================================
  // STATS
  // ==========================================================

  const stats =
    useMemo(() => {
      const list =
        Array.isArray(rows)
          ? rows
          : [];

      return {
        pending:
          list.filter(
            (item) =>
              String(
                item.approval_status ||
                  ""
              ).toLowerCase() ===
              "pending"
          ).length,

        disconnections:
          list.filter(
            (item) =>
              String(
                item.action_type ||
                  ""
              ).toLowerCase() ===
              "disconnection"
          ).length,

        reconnections:
          list.filter(
            (item) =>
              String(
                item.action_type ||
                  ""
              ).toLowerCase() ===
              "reconnection"
          ).length,
      };
    }, [
      rows,
    ]);

  // ==========================================================
  // PAGINATION
  // ==========================================================

  const currentPage =
    Number(
      pagination?.page ||
        filters.page ||
        1
    );

  const totalPages =
    Math.max(
      1,
      Number(
        pagination?.totalPages ||
          1
      )
    );

  const total =
    Number(
      pagination?.total ||
        0
    );

  const changePage = (
    page
  ) => {
    if (
      page < 1 ||
      page > totalPages ||
      page === currentPage
    ) {
      return;
    }

    setFilters(
      (previous) => ({
        ...previous,
        page,
      })
    );
  };

  // ==========================================================
  // NO PERMISSION
  // ==========================================================

  if (!canView) {
    return (
      <div className="hca-page">
        <Card className="hca-card">
          <Card.Body className="p-4">
            <h5 className="mb-2 fw-semibold">
              Connection Approvals
            </h5>

            <div className="text-muted">
              You do not have permission to view this page.
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div className="hca-page">
      {/* HEADER */}
      <div className="hca-header">
        <div>
          <h4 className="hca-title">
            Connection Approvals
          </h4>

          <div className="hca-subtitle">
            Review Water disconnection and reconnection requests assigned to you.
          </div>
        </div>

        <Button
          variant="light"
          className="hca-refresh-btn"
          disabled={loading}
          onClick={() =>
            loadRequests()
          }
        >
          <RefreshCcw
            size={16}
          />
          Refresh
        </Button>
      </div>

      {/* SUMMARY */}
      <Row className="g-3 mb-3">
        <Col
          xs={12}
          sm={6}
          xl={3}
        >
          <div className="hca-stat-card">
            <div className="hca-stat-label">
              Total Assigned
            </div>
            <div className="hca-stat-value">
              {total}
            </div>
          </div>
        </Col>

        <Col
          xs={12}
          sm={6}
          xl={3}
        >
          <div className="hca-stat-card">
            <div className="hca-stat-label">
              Pending
            </div>
            <div className="hca-stat-value">
              {stats.pending}
            </div>
          </div>
        </Col>

        <Col
          xs={12}
          sm={6}
          xl={3}
        >
          <div className="hca-stat-card">
            <div className="hca-stat-label">
              Disconnections
            </div>
            <div className="hca-stat-value">
              {stats.disconnections}
            </div>
          </div>
        </Col>

        <Col
          xs={12}
          sm={6}
          xl={3}
        >
          <div className="hca-stat-card">
            <div className="hca-stat-label">
              Reconnections
            </div>
            <div className="hca-stat-value">
              {stats.reconnections}
            </div>
          </div>
        </Col>
      </Row>

      {/* FILTERS */}
      <Card className="hca-card mb-3">
        <Card.Body className="hca-filter-body">
          <Row className="g-3 align-items-end">
            <Col
              xs={12}
              lg={6}
            >
              <Form
                onSubmit={
                  submitSearch
                }
              >
                <Form.Label className="hca-label">
                  Search
                </Form.Label>

                <InputGroup className="hca-search-group">
                  <InputGroup.Text>
                    <Search size={16} />
                  </InputGroup.Text>

                  <Form.Control
                    className="hca-control"
                    placeholder="House ID, door no, family head, mobile, QR..."
                    value={searchInput}
                    onChange={(event) =>
                      setSearchInput(
                        event.target.value
                      )
                    }
                  />

                  {searchInput && (
                    <Button
                      type="button"
                      variant="light"
                      className="hca-search-clear"
                      onClick={
                        clearSearch
                      }
                    >
                      Clear
                    </Button>
                  )}

                  <Button
                    type="submit"
                    className="hca-search-btn"
                  >
                    Search
                  </Button>
                </InputGroup>
              </Form>
            </Col>

            <Col
              xs={12}
              md={6}
              lg={3}
            >
              <Form.Label className="hca-label">
                Status
              </Form.Label>

              <Form.Select
                className="hca-control"
                value={
                  filters.status
                }
                onChange={(event) =>
                  setFilters(
                    (previous) => ({
                      ...previous,
                      status:
                        event.target.value,
                      page: 1,
                    })
                  )
                }
              >
                <option value="pending">
                  Pending
                </option>
                <option value="approved">
                  Approved
                </option>
                <option value="rejected">
                  Rejected
                </option>
                <option value="">
                  All
                </option>
              </Form.Select>
            </Col>

            <Col
              xs={12}
              md={6}
              lg={3}
            >
              <Form.Label className="hca-label">
                Action
              </Form.Label>

              <Form.Select
                className="hca-control"
                value={
                  filters.action_type
                }
                onChange={(event) =>
                  setFilters(
                    (previous) => ({
                      ...previous,
                      action_type:
                        event.target.value,
                      page: 1,
                    })
                  )
                }
              >
                <option value="">
                  All
                </option>
                <option value="disconnection">
                  Disconnection
                </option>
                <option value="reconnection">
                  Reconnection
                </option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* TABLE */}
      <Card className="hca-card">
        <Card.Body className="p-0">
          {loading ? (
            <div className="hca-loading">
              <Spinner
                animation="border"
                size="sm"
              />
              <span>
                Loading approval requests...
              </span>
            </div>
          ) : (
            <>
              <div className="hca-table-wrap">
                <Table
                  hover
                  responsive={false}
                  className="hca-table"
                >
                  <thead>
                    <tr>
                      <th>Request ID</th>
                      <th>House</th>
                      <th>Family Head</th>
                      <th>Mobile</th>
                      <th>Action</th>
                      <th>Action Date</th>
                      <th>Reason</th>
                      <th>Reading</th>
                      <th>Charge</th>
                      <th>Status</th>
                      <th>Requested On</th>
                      <th className="hca-actions-th">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {!rows?.length ? (
                      <tr>
                        <td
                          colSpan={12}
                          className="hca-empty-cell"
                        >
                          <div className="hca-empty-icon">
                            <CheckCircle size={20} />
                          </div>

                          <strong>
                            No approval requests found
                          </strong>

                          <span>
                            There are no requests matching the selected filters.
                          </span>
                        </td>
                      </tr>
                    ) : (
                      rows.map(
                        (request) => {
                          const isPending =
                            String(
                              request.approval_status ||
                                ""
                            ).toLowerCase() ===
                            "pending";

                          return (
                            <tr
                              key={
                                request.request_id
                              }
                            >
                              <td className="fw-semibold">
                                #
                                {
                                  request.request_id
                                }
                              </td>

                              <td>
                                <div className="hca-house-main">
                                  {safe(
                                    request.door_no
                                  )}
                                </div>

                                <div className="hca-house-sub">
                                  Home #
                                  {safe(
                                    request.home_id
                                  )}
                                </div>
                              </td>

                              <td>
                                {safe(
                                  request.family_head
                                )}
                              </td>

                              <td>
                                {safe(
                                  request.mobile
                                )}
                              </td>

                              <td>
                                <span className="hca-action-pill">
                                  {formatAction(
                                    request.action_type
                                  )}
                                </span>
                              </td>

                              <td>
                                {formatDate(
                                  request.action_date
                                )}
                              </td>

                              <td className="hca-reason-cell">
                                {safe(
                                  request.reason
                                )}
                              </td>

                              <td>
                                {safe(
                                  request.current_reading
                                )}
                              </td>

                              <td className="fw-semibold">
                                {money(
                                  request.charge_amount
                                )}
                              </td>

                              <td>
                                <StatusBadge
                                  status={
                                    request.approval_status
                                  }
                                />
                              </td>

                              <td>
                                {formatDateTime(
                                  request.created_at
                                )}
                              </td>

                              <td className="hca-actions-td">
                                <div className="hca-actions">
                                  <Button
                                    variant="light"
                                    className="hca-icon-btn view"
                                    onClick={() =>
                                      openDetails(
                                        request
                                      )
                                    }
                                    title="View"
                                  >
                                    <Eye size={16} />
                                  </Button>

                                  {isPending &&
                                    canUpdate && (
                                      <>
                                        <Button
                                          variant="light"
                                          className="hca-icon-btn approve"
                                          onClick={() =>
                                            openActionModal(
                                              request,
                                              "approve"
                                            )
                                          }
                                          title="Approve"
                                        >
                                          <CheckCircle
                                            size={16}
                                          />
                                        </Button>

                                        <Button
                                          variant="light"
                                          className="hca-icon-btn reject"
                                          onClick={() =>
                                            openActionModal(
                                              request,
                                              "reject"
                                            )
                                          }
                                          title="Reject"
                                        >
                                          <XCircle
                                            size={16}
                                          />
                                        </Button>
                                      </>
                                    )}
                                </div>
                              </td>
                            </tr>
                          );
                        }
                      )
                    )}
                  </tbody>
                </Table>
              </div>

              {total > 0 && (
                <div className="hca-footer">
                  <div className="hca-total-text">
                    Total{" "}
                    <strong>
                      {total}
                    </strong>{" "}
                    request(s)
                  </div>

                  <Pagination className="hca-pagination mb-0">
                    <Pagination.Prev
                      disabled={
                        currentPage <=
                        1
                      }
                      onClick={() =>
                        changePage(
                          currentPage - 1
                        )
                      }
                    >
                      <ChevronLeft
                        size={15}
                      />
                    </Pagination.Prev>

                    {Array.from(
                      {
                        length:
                          totalPages,
                      },
                      (_, index) =>
                        index + 1
                    )
                      .slice(
                        Math.max(
                          0,
                          currentPage - 3
                        ),
                        Math.min(
                          totalPages,
                          currentPage + 2
                        )
                      )
                      .map(
                        (page) => (
                          <Pagination.Item
                            key={page}
                            active={
                              page ===
                              currentPage
                            }
                            onClick={() =>
                              changePage(
                                page
                              )
                            }
                          >
                            {page}
                          </Pagination.Item>
                        )
                      )}

                    <Pagination.Next
                      disabled={
                        currentPage >=
                        totalPages
                      }
                      onClick={() =>
                        changePage(
                          currentPage + 1
                        )
                      }
                    >
                      <ChevronRight
                        size={15}
                      />
                    </Pagination.Next>
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* ======================================================
          DETAILS MODAL
      ====================================================== */}

      <Modal
        show={detailModal}
        onHide={closeDetails}
        centered
        size="lg"
        className="hca-modal"
      >
        <Modal.Header closeButton>
          <div>
            <Modal.Title>
              Connection Request Details
            </Modal.Title>

            <div className="hca-modal-subtitle">
              Review house, request and billing information before taking action.
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          {detailLoading ? (
            <div className="hca-modal-loading">
              <Spinner
                animation="border"
                size="sm"
              />
              <span>
                Loading request details...
              </span>
            </div>
          ) : selectedRequest ? (
            <>
              <div className="hca-modal-topline">
                <div>
                  <span>Request</span>
                  <strong>
                    #
                    {safe(
                      selectedRequest.request_id
                    )}
                  </strong>
                </div>

                <span className="hca-action-pill">
                  {formatAction(
                    selectedRequest.action_type
                  )}
                </span>

                <StatusBadge
                  status={
                    selectedRequest.approval_status
                  }
                />
              </div>

              <Row className="g-3">
                <Col md={6}>
                  <div className="hca-section-card">
                    <div className="hca-section-title">
                      House Details
                    </div>

                    <div className="hca-detail-grid">
                      <DetailItem
                        label="House ID"
                        value={
                          selectedRequest.home_id
                        }
                      />

                      <DetailItem
                        label="Door No"
                        value={
                          selectedRequest.door_no
                        }
                      />

                      <DetailItem
                        label="Family Head"
                        value={
                          selectedRequest.family_head
                        }
                      />

                      <DetailItem
                        label="Mobile"
                        value={
                          selectedRequest.mobile
                        }
                      />

                      <DetailItem
                        label="Water Status"
                        value={
                          selectedRequest.water_connection_status
                        }
                      />
                    </div>
                  </div>
                </Col>

                <Col md={6}>
                  <div className="hca-section-card">
                    <div className="hca-section-title">
                      Request Information
                    </div>

                    <div className="hca-detail-grid">
                      <DetailItem
                        label="Action Date"
                        value={formatDate(
                          selectedRequest.action_date
                        )}
                      />

                      <DetailItem
                        label="Assigned Approver"
                        value={`#${safe(
                          selectedRequest.assigned_approver_user_id
                        )}`}
                      />

                      <DetailItem
                        label="Current Reading"
                        value={
                          selectedRequest.current_reading
                        }
                      />

                      <DetailItem
                        label="Connection Charge"
                        value={money(
                          selectedRequest.charge_amount
                        )}
                        accent
                      />

                      <DetailItem
                        label="Water Balance"
                        value={money(
                          selectedRequest.water_balance
                        )}
                        accent
                      />
                    </div>
                  </div>
                </Col>
              </Row>

              <div className="hca-note-card mt-3">
                <div>
                  <span>Reason</span>
                  <strong>
                    {safe(
                      selectedRequest.reason
                    )}
                  </strong>
                </div>

                <div>
                  <span>Remarks</span>
                  <strong>
                    {safe(
                      selectedRequest.remarks
                    )}
                  </strong>
                </div>

                {selectedRequest.approval_remarks && (
                  <div>
                    <span>
                      Approval Remarks
                    </span>

                    <strong>
                      {
                        selectedRequest.approval_remarks
                      }
                    </strong>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="hca-modal-loading">
              Request details unavailable.
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="light"
            className="hca-secondary-btn"
            onClick={
              closeDetails
            }
          >
            Close
          </Button>

          {selectedRequest &&
            String(
              selectedRequest.approval_status ||
                ""
            ).toLowerCase() ===
              "pending" &&
            canUpdate && (
              <>
                <Button
                  variant="outline-danger"
                  className="hca-danger-outline-btn"
                  onClick={() => {
                    const request =
                      selectedRequest;

                    closeDetails();

                    openActionModal(
                      request,
                      "reject"
                    );
                  }}
                >
                  Reject
                </Button>

                <Button
                  variant="success"
                  className="hca-success-btn"
                  onClick={() => {
                    const request =
                      selectedRequest;

                    closeDetails();

                    openActionModal(
                      request,
                      "approve"
                    );
                  }}
                >
                  Approve
                </Button>
              </>
            )}
        </Modal.Footer>
      </Modal>

      {/* ======================================================
          APPROVE / REJECT CONFIRMATION MODAL
      ====================================================== */}

      <Modal
        show={actionModal.show}
        onHide={closeActionModal}
        centered
        className="hca-modal hca-confirm-modal"
        backdrop={
          approving || rejecting
            ? "static"
            : true
        }
        keyboard={
          !approving &&
          !rejecting
        }
      >
        <Modal.Header
          closeButton={
            !approving &&
            !rejecting
          }
        >
          <div>
            <Modal.Title>
              {actionModal.type ===
              "approve"
                ? "Confirm Approval"
                : "Confirm Rejection"}
            </Modal.Title>

            <div className="hca-modal-subtitle">
              {actionModal.type ===
              "approve"
                ? "Approve and process this Water connection request."
                : "Reject this Water connection request."}
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          <div
            className={`hca-confirm-banner ${
              actionModal.type ===
              "approve"
                ? "approve"
                : "reject"
            }`}
          >
            {actionModal.type ===
            "approve" ? (
              <CheckCircle
                size={20}
              />
            ) : (
              <XCircle size={20} />
            )}

            <div>
              <strong>
                {actionModal.type ===
                "approve"
                  ? "Approve this request?"
                  : "Reject this request?"}
              </strong>

              <span>
                {actionModal.type ===
                "approve"
                  ? "The backend will process the Water connection change after approval."
                  : "The request will be marked as rejected and will not be processed."}
              </span>
            </div>
          </div>

          <div className="hca-confirm-summary">
            <DetailItem
              label="Request ID"
              value={`#${safe(
                actionModal.request
                  ?.request_id
              )}`}
            />

            <DetailItem
              label="Action"
              value={formatAction(
                actionModal.request
                  ?.action_type
              )}
            />

            <DetailItem
              label="House"
              value={
                actionModal.request
                  ?.door_no
              }
            />

            <DetailItem
              label="Family Head"
              value={
                actionModal.request
                  ?.family_head
              }
            />

            <DetailItem
              label="Current Reading"
              value={
                actionModal.request
                  ?.current_reading
              }
            />

            <DetailItem
              label="Charge"
              value={money(
                actionModal.request
                  ?.charge_amount
              )}
              accent
            />
          </div>

          <Form.Group className="mt-3">
            <Form.Label className="hca-label">
              {actionModal.type ===
              "reject"
                ? "Rejection Remarks *"
                : "Approval Remarks"}
            </Form.Label>

            <Form.Control
              as="textarea"
              rows={3}
              className="hca-textarea"
              value={
                approvalRemarks
              }
              disabled={
                approving ||
                rejecting
              }
              placeholder={
                actionModal.type ===
                "reject"
                  ? "Enter reason for rejection"
                  : "Enter approval remarks (optional)"
              }
              onChange={(event) =>
                setApprovalRemarks(
                  event.target.value
                )
              }
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="light"
            className="hca-secondary-btn"
            disabled={
              approving ||
              rejecting
            }
            onClick={
              closeActionModal
            }
          >
            Cancel
          </Button>

          <Button
            variant={
              actionModal.type ===
              "approve"
                ? "success"
                : "danger"
            }
            className={
              actionModal.type ===
              "approve"
                ? "hca-success-btn"
                : "hca-danger-btn"
            }
            disabled={
              approving ||
              rejecting ||
              (
                actionModal.type ===
                  "reject" &&
                !approvalRemarks.trim()
              )
            }
            onClick={
              submitAction
            }
          >
            {approving || rejecting ? (
              <>
                <Spinner
                  size="sm"
                  animation="border"
                  className="me-2"
                />
                Processing...
              </>
            ) : actionModal.type ===
              "approve" ? (
              <>
                <CheckCircle
                  size={17}
                  className="me-2"
                />
                Approve Request
              </>
            ) : (
              <>
                <XCircle
                  size={17}
                  className="me-2"
                />
                Reject Request
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
